import React from "react";
import ReactDOM from "react-dom";
import ReactTooltip from "react-tooltip";
import { deleteTooltipContent, setTooltipContent } from "./tooltipRegistry";

/**
 * Props only a dedicated `ReactTooltip` instance can serve: custom show/hide events, a
 * non-default effect/type, or bespoke positioning. Tooltips using them are rare (a couple of
 * click-triggered popovers) and at most one is on screen at a time, so they keep an instance
 * of their own. Everything else shares the app-wide instance — see `tooltipRegistry` for why
 * that matters.
 */
const STANDALONE_PROPS = ["event", "eventOff", "globalEvent", "effect", "type", "offset", "overridePosition"];

export function Tooltip(props) {
    // Stable per call site: these props are constants wherever they are used.
    const needsOwnInstance = STANDALONE_PROPS.some((prop) => typeof props[prop] !== "undefined");

    return needsOwnInstance ? <StandaloneTooltip {...props}/> : <SharedTooltip {...props}/>;
}

/**
 * Registers content for the shared instance and renders no DOM of its own, so a screen with
 * hundreds of tooltips still holds a single set of listeners.
 *
 * Per-tooltip presentation (`place`, `className`) lives on the trigger element instead, as
 * react-tooltip's own `data-place` / `data-class` attributes — the shared instance reads
 * those off whichever trigger is hovered.
 */
function SharedTooltip({ id, content }) {
    // Registered on every render rather than only on mount: `content` is normally a closure
    // over the owning component's props/state and would otherwise go stale after an update.
    setTooltipContent(id, content);

    const latestContent = React.useRef(content);
    latestContent.current = content;

    React.useEffect(() => () => deleteTooltipContent(id, latestContent.current), [id]);

    return null;
}

function StandaloneTooltip(props) {
    const place = props.place || 'top';
    const customProps = {};

    if (props.event) {
        customProps.event = props.event;
    }

    if (props.eventOff) {
        customProps.eventOff = props.eventOff;
    }

    if (props.globalEvent) {
        customProps.globalEvent = props.globalEvent;
    }

    if (props.globalEventOff) {
        customProps.globalEventOff = props.globalEventOff;
    }

    if (typeof props.scrollHide !== 'undefined') {
        customProps.scrollHide = props.scrollHide;
    }

    if (typeof props.resizeHide !== 'undefined') {
        customProps.resizeHide = props.resizeHide;
    }

    if (typeof props.disabled !== 'undefined') {
        customProps.disable = props.disabled;
    }

    if (typeof props.offset !== 'undefined') {
        customProps.offset = props.offset;
    }

    if (typeof props.overridePosition !== 'undefined') {
        customProps.overridePosition = props.overridePosition;
    }

    if (typeof props.effect !== 'undefined') {
        customProps.effect = props.effect;
    }

    if (typeof props.type !== 'undefined') {
        customProps.type = props.type;
    }

    // Let react-tooltip keep its own (unlayered) styles so it handles positioning, show/hide
    // and the arrow itself, and feed it the Gaia colours/padding through its props. The Gaia
    // `ga-tooltip` class plus the few overrides in migration_gaia.scss handle the rest
    // (radius, max-width, font) with a selector specific enough to win.
    // NOTE: we deliberately do NOT add Gaia's `ga-tooltip--{place}-center` arrow modifier.
    // react-tooltip flips `place` dynamically (e.g. top -> right near a viewport edge) and
    // updates its own `place-*` class, but the Gaia modifier would stay fixed at the initial
    // `place`, so the two arrows would render in different spots (a stray square). react-tooltip
    // owns the arrow; Gaia only styles the bubble.
    const className = ['ga-tooltip', props.borderClass, props.className]
        .filter(Boolean)
        .join(' ');

    // react-tooltip renders inline in the DOM, so it inherits any ancestor stacking context
    // or reduced opacity and can end up clipped/faded behind sibling panels. It binds to its
    // trigger globally (by `data-for`/`id`) and positions with viewport-fixed coordinates, so
    // rendering it through a portal on `document.body` keeps it above everything regardless of
    // where the trigger lives in the tree.
    return ReactDOM.createPortal(
        <ReactTooltip
            role="tooltip"
            globalEventOff={"keydown"}
            place={place}
            id={props.id}
            className={className}
            delayHide={300}
            backgroundColor={"var(--ga-color-surface-action-hover)"}
            textColor={"var(--ga-color-text-on-action)"}
            arrowColor={"var(--ga-color-surface-action-hover)"}
            padding={"calc(0.4rem * 2)"}
            effect='solid'
            getContent={props.content}
            {...customProps}
        />,
        document.body
    )
}